import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  StyleSheet, ScrollView, View, Text,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation } from '@react-navigation/native';
import Moment from 'moment';
import TopSection, { screenMaxWidth, TopSectionProps } from '../components/TopSection';
import BackgroundImage from '../components/BackgroundImage';
import Colors from '../constants/Colors';
import { DateRange, DateSelectionRange, updateDateRange } from '../redux/dateRangeSlice';
import { updateDateComparisonRange } from '../redux/dateComparisonRangeSlice';
import ScreenEmptyState from '../components/ScreenEmptyState';
import LocationOccupancyDashboard from '../components/Occupancy/LocationOccupancyDashboard';
import GetMetric, { AggregationFunction } from '../requests/Sensors/GetMetric';
import PressableStyled from '../components/PressableStyled';
import { HappyScoreCategory, Metric } from '../constants/Types';
import MetricLineGraph from '../graphs/MetricLineGraph';
import isMobile from '../components/IsMobile';
import MobileHeader from './InternalEnvironment/components/MobileHeader';
import MobileDashboard from '../components/Occupancy/MobileDashboard';
import FixedDateComponent from '../components/FixedDateComponent';
import GetEntireLocationStructure from '../components/GetEntireLocationStructure';
import { updateLocations } from '../redux/locationStructureSlice';
import { AppScreen } from '../navigation/LinkingConfiguration';

export default function OccupantsScreen({ route }) {
  const navigation = useNavigation();
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));
  const compareRange: DateRange = useSelector((state: { dateComparisonRange: DateRange }) => (state
    .dateComparisonRange));
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const buildings = useSelector((state: {
    locations: { buildings: Record<string, { ExternalId?: string | null }> }
  }) => state.locations.buildings);
  const [locationExternalId, setLocationExternalId] = useState<string | null>(null);
  const previousSelectedBuildingIdRef = useRef<string | null>(null);
  const dispatch = useDispatch();
  const occupancyRef = useRef<View>(null);
  const bookingsRef = useRef<View>(null);
  const mostUtilisedRef = useRef<View>(null);
  const scrollViewRef = useRef(null);
  const setRange = (dates: DateRange) => dispatch(updateDateRange(dates));
  const setCompareRange = (dates: DateRange) => dispatch(updateDateComparisonRange(dates));
  const notEmpty: boolean | undefined | null = useSelector((state:
    { emptyState: { notEmpty: boolean } }) => state.emptyState.notEmpty);

  const onConfirm = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => setRange(
      { start, end, selectedRange },
    ),
    [setRange],
  );

  const onCompareConfirm = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => setCompareRange(
      { start, end, selectedRange },
    ),
    [setCompareRange],
  );

  const [occupancyData, setOccupancyData] = useState([]);
  const [occupancyComparisonData, setOccupancyComparisonData] = useState([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDev, setIsDev] = useState<boolean>(false);
  const [showGraphs, setShowGraphs] = useState<boolean>(false);

  useEffect(() => {
    if (!isMobile && !loading) return;
    setOccupancyData([]);
    GetMetric({
      metric: Metric.OCCUPANCY,
      range,
      externalId: locationExternalId,
      aggregationFunction: AggregationFunction.MAX,
    })?.then((data) => {
      const newData = Object.assign([], data);
      setOccupancyData(newData);
      setLoading(false);
    });
  }, [range, locationExternalId]);

  useEffect(() => {
    if (!isMobile && !loading) return;
    setOccupancyComparisonData([]);
    GetMetric({
      metric: Metric.OCCUPANCY,
      range: compareRange,
      externalId: locationExternalId,
      aggregationFunction: AggregationFunction.MAX,
    })?.then((data) => {
      const newData = Object.assign([], data);
      setOccupancyComparisonData(newData);
      setLoading(false);
    });
  }, [compareRange, locationExternalId]);

  useEffect(() => {
    AsyncStorage.getItem('environment').then((env) => {
      setIsDev(env === 'dev');
    });
    AsyncStorage.getItem('clientName').then((name: string) => {
      if (name?.toLowerCase().includes('hereworks')) {
        setShowGraphs(true);
      }
    });
    const unsubscribe = navigation.addListener('focus', () => {
      GetEntireLocationStructure((locations) => dispatch(updateLocations(locations)));
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!selectedBuildingId) {
      previousSelectedBuildingIdRef.current = null;
      setLocationExternalId(null);
      return;
    }

    const buildingExternalId: string | null = buildings?.[selectedBuildingId]?.ExternalId ?? null;
    const buildingChanged: boolean = previousSelectedBuildingIdRef.current !== selectedBuildingId;
    previousSelectedBuildingIdRef.current = selectedBuildingId;

    if (buildingChanged) {
      setLocationExternalId(buildingExternalId);
      return;
    }

    if (!locationExternalId && buildingExternalId) {
      setLocationExternalId(buildingExternalId);
    }
  }, [selectedBuildingId, buildings, locationExternalId]);

  useEffect(() => {
    setTimeout(() => {
      const graph = (route.params as any)?.graph;
      if (occupancyRef.current && graph === 'occupancy') {
        occupancyRef.current.measureLayout(
          scrollViewRef.current.getInnerViewNode(),
          (x, y) => {
            scrollViewRef.current.scrollTo({ x: 0, y, animated: true });
          },
        );
      } else if (bookingsRef.current && graph === 'bookings') {
        bookingsRef.current.measureLayout(
          scrollViewRef.current.getInnerViewNode(),
          (x, y) => {
            scrollViewRef.current.scrollTo({ x: 0, y, animated: true });
          },
        );
      } else if (mostUtilisedRef.current && graph === 'mostutilised') {
        mostUtilisedRef.current.measureLayout(
          scrollViewRef.current.getInnerViewNode(),
          (x, y) => {
            scrollViewRef.current.scrollTo({ x: 0, y, animated: true });
          },
        );
      }
    }, 500);
  }, [route]);

  const topSectionProps: TopSectionProps = {
    screen: HappyScoreCategory.OCCUPANCY,
    liveEnabled: true,
  };

  if (isMobile) {
    const [graphTitle, setGraphTitle] = useState<string | null>(null);
    const [locationId, setLocationId] = useState<string | null>(null);
    const [locationCapacity, setLocationCapacity] = useState<number | null>(null);
    const [liveCount, setLiveCount] = useState<number | null>(null);

    useEffect(() => {
      setLiveCount(null);
      if (!locationExternalId) return;
      GetMetric({
        metric: Metric.OCCUPANCY,
        // @ts-ignore
        range: { start: Moment().subtract(60, 'minutes'), end: Moment() },
        interval: '1m',
        externalId: locationExternalId,
        aggregationFunction: AggregationFunction.MAX,
      })?.then((data) => setLiveCount(data[data.length - 1].value));
    }, [locationExternalId]);

    return <MobileHeader
      screen={HappyScoreCategory.OCCUPANCY}
      title='Occupancy'
      happyScoreMetric='group2BuildingOccupancy'
      {...{
        graphTitle, setLocationId, setLocationCapacity, setLocationExternalId,
      }}
      graphData={occupancyData
        .map(({ timestamp, value }, index: number) => ({ index, value, date: timestamp }))}
      graphComparisonData={occupancyComparisonData
        .map(({ timestamp, value }, index: number) => ({ index, value, date: timestamp }))}
      overview={<MobileDashboard
        showGraph={() => setGraphTitle('Occupancy details')}
        {...{
          locationId: locationId ?? selectedBuildingId,
          locationCapacity,
          locationExternalId,
          liveCount,
        }}
      />}
      showContent={() => setGraphTitle(null)}
    />;
  }

  return !notEmpty ? <ScreenEmptyState large /> : <View style={{ flex: 1 }}>
    <ScrollView
      style={styles.container}
      ref={scrollViewRef}
    >
      <BackgroundImage />
      {isDev ? <PressableStyled
        onPress={() => {
          navigation.navigate(AppScreen.WIDGET_SHOWCASE as any);
        }}
        style={{
          padding: 10, backgroundColor: Colors.light.hereworksWhite100, margin: 24, alignSelf: 'flex-start', borderRadius: 8,
        }}>
        <Text style={{ fontFamily: 'Roboto_500Medium' }}>Show dev UI</Text>
      </PressableStyled> : null}
      <TopSection {...topSectionProps} />
      <View style={[styles.content, { maxWidth: screenMaxWidth }]}>
        <View ref={mostUtilisedRef}>
          <LocationOccupancyDashboard
            {...{ occupancyData, onConfirm, onCompareConfirm }}
            datePickerShown={false}
          />
        </View>
        {showGraphs ? <>
          <View style={[styles.graphCard, { marginHorizontal: 0 }]} ref={occupancyRef}>
            <MetricLineGraph
              {...{ range, compareRange }}
              title='Occupancy'
              metric={Metric.OCCUPANCY}
              hideTimeOnTooltip
            />
          </View>
          <View style={[styles.graphCard, { marginHorizontal: 0 }]} ref={bookingsRef}>
            <MetricLineGraph
              title='Desk Bookings'
              {...{ range, compareRange }}
              field='desksbookedcount'
              metric='smartway2desksbookedcount'
            />
          </View>
        </> : null}
      </View>
    </ScrollView>
    <FixedDateComponent
      onConfirm={onConfirm}
      onCompareConfirm={onCompareConfirm}
      liveEnabled
    />
  </View>;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.light.hereworksBackground,
    paddingBottom: 40,
  },
  content: {
    paddingHorizontal: 40,
  },
  graphCard: {
    borderRadius: 16,
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
    flex: 1,
    minWidth: 300,
    margin: 8,
    minHeight: 300,
  },
  grid: {
    marginHorizontal: -8,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
});
